import React, { useCallback, useEffect, useState } from 'react';
import {
    Badge, Button, Form, Input, InputNumber, Modal, Popconfirm,
    Select, Space, Table, Tooltip, message
} from 'antd';
import {
    DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined
} from '@ant-design/icons';
import api from '../../../../services/api';

const ProductLineSettings = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const loadData = useCallback(async (page = 1, pageSize = 50, nextSearch = '', nextStatus = 'all') => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize)
            });
            if (nextSearch.trim()) params.set('search', nextSearch.trim());
            if (nextStatus !== 'all') params.set('isActive', String(nextStatus === 'active'));
            const response = await api.get(`/product-lines?${params.toString()}`);
            setRows(response.data?.rows || []);
            setPagination({
                current: response.data?.pagination?.page || page,
                pageSize: response.data?.pagination?.pageSize || pageSize,
                total: response.data?.count || 0
            });
        } catch (error) {
            message.error(error.response?.data?.message || 'Không thể tải dòng sản phẩm');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ isActive: true, sortOrder: 0 });
        setModalOpen(true);
    };

    const openEdit = record => {
        setEditing(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            isActive: record.isActive,
            sortOrder: record.sortOrder
        });
        setModalOpen(true);
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            if (editing) {
                await api.put(`/product-lines/${editing.id}`, values);
                message.success('Đã cập nhật dòng sản phẩm');
            } else {
                await api.post('/product-lines', values);
                message.success('Đã tạo dòng sản phẩm');
            }
            setModalOpen(false);
            await loadData(pagination.current, pagination.pageSize, search, status);
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.response?.data?.message || 'Không thể lưu dòng sản phẩm');
        }
    };

    const remove = async id => {
        try {
            await api.delete(`/product-lines/${id}`);
            message.success('Đã xóa dòng sản phẩm');
            await loadData(pagination.current, pagination.pageSize, search, status);
        } catch (error) {
            message.error(error.response?.data?.message || 'Không thể xóa dòng sản phẩm');
        }
    };

    const columns = [
        { title: 'Dòng sản phẩm', dataIndex: 'name', key: 'name' },
        { title: 'Mô tả', dataIndex: 'description', key: 'description', ellipsis: true },
        {
            title: 'Danh mục',
            dataIndex: 'categoryCount',
            key: 'categoryCount',
            width: 100,
            align: 'right'
        },
        {
            title: 'Thứ tự',
            dataIndex: 'sortOrder',
            key: 'sortOrder',
            width: 90,
            align: 'right'
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 130,
            render: value => <Badge status={value ? 'success' : 'default'} text={value ? 'Hoạt động' : 'Không hoạt động'} />
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 100,
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Chỉnh sửa">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Popconfirm
                        title="Xóa dòng sản phẩm này?"
                        description="Chỉ có thể xóa khi không còn danh mục liên kết."
                        okText="Xóa"
                        cancelText="Hủy"
                        onConfirm={() => remove(record.id)}
                    >
                        <Tooltip title="Xóa">
                            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Space wrap>
                    <Input.Search
                        allowClear
                        placeholder="Tìm dòng sản phẩm"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        onSearch={() => loadData(1, pagination.pageSize, search, status)}
                        style={{ width: 280 }}
                    />
                    <Select
                        value={status}
                        onChange={value => {
                            setStatus(value);
                            loadData(1, pagination.pageSize, search, value);
                        }}
                        style={{ width: 160 }}
                        options={[
                            { value: 'all', label: 'Tất cả trạng thái' },
                            { value: 'active', label: 'Hoạt động' },
                            { value: 'inactive', label: 'Không hoạt động' }
                        ]}
                    />
                    <Tooltip title="Tải lại">
                        <Button icon={<ReloadOutlined />} onClick={() => loadData(pagination.current, pagination.pageSize, search, status)} />
                    </Tooltip>
                </Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    Thêm dòng sản phẩm
                </Button>
            </Space>

            <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100'],
                    showTotal: total => `${total} dòng sản phẩm`
                }}
                onChange={next => loadData(next.current, next.pageSize, search, status)}
            />

            <Modal
                title={editing ? 'Chỉnh sửa dòng sản phẩm' : 'Thêm dòng sản phẩm'}
                open={modalOpen}
                onOk={save}
                onCancel={() => setModalOpen(false)}
                confirmLoading={loading}
                okText={editing ? 'Cập nhật' : 'Tạo mới'}
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Tên" rules={[{ required: true, message: 'Vui lòng nhập tên' }]}>
                        <Input placeholder="Ví dụ: Hat, Soccer Jersey" />
                    </Form.Item>
                    <Form.Item name="description" label="Mô tả">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Space align="start">
                        <Form.Item name="sortOrder" label="Thứ tự">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="isActive" label="Trạng thái">
                            <Select
                                style={{ width: 160 }}
                                options={[
                                    { value: true, label: 'Hoạt động' },
                                    { value: false, label: 'Không hoạt động' }
                                ]}
                            />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

export default ProductLineSettings;
